import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  getRoom, getRoomSignalingUrl, getIceServers,
} from 'Api/roomsApi';
import {
  overlay, center, card, heading, body,
  primaryBtn, secondaryBtn, ctaRow, videoFrame, videoTag, isMobileQuery,
} from './styles';
import { InterviewCall } from './InterviewCall';

// Guest entry for an interview-record session.
//
// URL: /interview/join/:roomId — anyone with the link gets in. No auth.
//
// State machine:
//   1. checking      — GET /rooms/{id}; validates the link.
//   2. ready-to-join — room exists + open; show camera preview + Join CTA.
//   3. joining       — Join clicked; getUserMedia + WS open as 'guest'.
//   4. waiting       — WS open, host either present or expected shortly.
//                      (PR 3 takes over here and starts the WebRTC.)
//   5. error         — link bad / room closed / room full / permissions.
//
// The page intentionally does NOT auto-acquire camera on mount — many
// guests will land here on mobile during a busy moment, and a surprise
// "browser wants to use your camera" banner is hostile UX. They tap Join
// to give explicit consent.
export const InterviewJoin = () => {
  const { roomId } = useParams();
  const [isMobile, setIsMobile] = useState(isMobileQuery);
  useEffect(() => {
    const onResize = () => setIsMobile(isMobileQuery());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [phase, setPhase] = useState('checking');
  const [error, setError] = useState('');
  const [hostConnected, setHostConnected] = useState(false);
  const [ws, setWs] = useState(null);
  const [iceServers, setIceServers] = useState([]);
  const [stream, setStream] = useState(null);
  const [inCall, setInCall] = useState(false);
  // Pre-join lobby choices (Zoom-style): the guest can decide to join with
  // mic muted and/or camera off before granting permission. These are
  // INTENT only — we apply them to the real tracks once Join acquires the
  // stream, and InterviewCall picks the state up from the tracks.
  const [joinMuted, setJoinMuted] = useState(false);
  const [joinCameraOff, setJoinCameraOff] = useState(false);
  // Optional display name — shown to the host (and on the guest's own
  // camera-off avatar) in place of the generic "Guest".
  const [guestName, setGuestName] = useState('');
  // Bumped on each peer-joined(host) so InterviewCall remounts with a
  // fresh PeerConnection if the host reconnects. See InterviewHost for
  // the symmetric reasoning.
  const [negotiationKey, setNegotiationKey] = useState(0);
  // End-of-session state for the guest. Set when:
  //   • 'finished'    — host sent {type:'bye'} (intentional Stop/Leave)
  //   • 'disconnected'— host's WS dropped without a bye (tab close,
  //                     network loss). Two different messages: the
  //                     finished one is celebratory, disconnected is
  //                     apologetic. Once set, never overwritten — a
  //                     trailing peer-left after a bye is ignored.
  const [sessionEnded, setSessionEnded] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const wsRef = useRef(null);

  const teardown = useCallback(() => {
    try { if (wsRef.current) wsRef.current.close(); } catch (e) { /* noop */ }
    wsRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch (e) { /* noop */ }
      });
      streamRef.current = null;
    }
  }, []);

  // Validate the room exists + is joinable before showing the Join CTA.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!roomId) {
        setError('This link looks broken. Ask for a new one.');
        setPhase('error');
        return;
      }
      try {
        const r = await getRoom(roomId);
        if (cancelled) return;
        if (r.status !== 'open' && r.status !== 'active') {
          setError('This meeting has ended. Ask for a new link.');
          setPhase('error');
          return;
        }
        if (r.is_full && !r.host_connected) {
          // Guest slot taken by someone else.
          setError('Someone has already joined this meeting from another device.');
          setPhase('error');
          return;
        }
        setHostConnected(!!r.host_connected);
        setPhase('ready-to-join');
      } catch (e) {
        if (!cancelled) {
          setError('We couldn\'t find that meeting. Ask for a fresh link.');
          setPhase('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  // Teardown on unmount (back button, tab close, etc.).
  useEffect(() => () => teardown(), [teardown]);

  const handleJoin = async () => {
    setPhase('joining');
    setError('');
    // 1) Permissions + ICE servers in parallel. ICE is needed by
    //    InterviewCall when WebRTC starts, so we fetch up front.
    let acquired;
    let ice;
    try {
      [acquired, ice] = await Promise.all([
        navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
          audio: { echoCancellation: true, noiseSuppression: true },
        }),
        getIceServers().catch(() => null),
      ]);
    } catch (e) {
      setError('We need access to your camera and microphone to join.');
      setPhase('error');
      return;
    }
    // Apply the lobby choices to the freshly-acquired tracks before the
    // call starts, so the guest joins exactly how they picked. InterviewCall
    // reads these track states for its initial mute / camera-off UI.
    const aTrack = acquired.getAudioTracks()[0];
    if (aTrack) aTrack.enabled = !joinMuted;
    const vTrack = acquired.getVideoTracks()[0];
    if (vTrack) vTrack.enabled = !joinCameraOff;

    streamRef.current = acquired;
    setStream(acquired);
    setIceServers((ice && ice.iceServers) || []);
    if (videoRef.current) {
      videoRef.current.srcObject = acquired;
    }

    // 2) Signaling socket.
    let sock;
    try {
      sock = new WebSocket(getRoomSignalingUrl(roomId, 'guest'));
    } catch (e) {
      setError('Could not connect to the meeting. Please try again.');
      setPhase('error');
      return;
    }
    wsRef.current = sock;
    sock.onopen = () => {
      setWs(sock);
      setPhase('waiting');
    };
    sock.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg && msg.type === 'peer-joined' && msg.role === 'host') {
          setHostConnected(true);
          // Fresh mount on host re-join, mirrors the host-side logic.
          setNegotiationKey((k) => k + 1);
        }
        if (msg && msg.type === 'peer-left' && msg.role === 'host') {
          setHostConnected(false);
          // peer-left after a bye is normal teardown — don't overwrite
          // the friendlier 'finished' state with 'disconnected'.
          setSessionEnded((prev) => prev || 'disconnected');
        }
        if (msg && msg.type === 'bye' && msg.from === 'host') {
          setSessionEnded((prev) => prev || 'finished');
        }
      } catch (e) { /* ignore unparseable messages */ }
    };
    sock.onclose = (ev) => {
      wsRef.current = null;
      setWs(null);
      // 4001 is our consumer's "slot taken / room invalid" close code.
      if (ev && ev.code === 4001) {
        setError('Someone else just joined this meeting.');
        setPhase('error');
      }
    };
  };

  // Auto-transition into the call view once we know the host is here +
  // the WS is open. The host page does the same on its side; both peers
  // arrive at InterviewCall, which negotiates the WebRTC handshake.
  useEffect(() => {
    if (!inCall && hostConnected && ws && stream && phase === 'waiting') {
      setInCall(true);
    }
  }, [inCall, hostConnected, ws, stream, phase]);

  // When the session ends (host finished or disconnected), tear down
  // the guest's call resources so the camera + mic light go out and
  // the dead PC stops chewing on ICE retries. The render below replaces
  // the call view with a full-screen end-state message.
  useEffect(() => {
    if (sessionEnded) teardown();
  }, [sessionEnded, teardown]);

  const handleLeaveCall = () => {
    teardown();
    // Guest has no onboarding context — just send them back to the home
    // page (they accessed this via a shared link, so leaving = exit app).
    if (typeof window !== 'undefined') window.location.href = '/';
  };

  // ── Session-ended view — takes priority over every other state so
  // a host that finishes mid-call doesn't fall back to the join page
  // or leave the call view stuck. Full-viewport, single CTA. ──
  if (sessionEnded) {
    const isFinished = sessionEnded === 'finished';
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'radial-gradient(circle at center, #1f2330, #07080c)',
          color: '#fff',
          fontFamily: 'inherit',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center',
          zIndex: 100,
        }}
      >
        <div style={{ width: '100%', maxWidth: '480px' }}>
          <div
            aria-hidden="true"
            style={{ fontSize: '3.5rem', lineHeight: 1, marginBottom: '14px' }}
          >
            {isFinished ? '✅' : '👋'}
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: isMobile ? '1.55rem' : '1.9rem',
              fontWeight: 800,
              letterSpacing: '-0.01em',
            }}
          >
            {isFinished ? 'Session complete' : 'The host left'}
          </h1>
          <p
            style={{
              margin: '12px auto 0',
              maxWidth: '380px',
              color: '#b9bdc7',
              fontSize: isMobile ? '0.95rem' : '1.05rem',
              lineHeight: 1.45,
            }}
          >
            {isFinished
              ? 'Thanks for being part of the conversation. Your host has the recording — they\'ll take it from here.'
              : 'Looks like the host\'s connection dropped. You can close this tab; they\'ll send a fresh link if they want to pick it up again.'}
          </p>
          <button
            type="button"
            onClick={handleLeaveCall}
            style={{
              ...primaryBtn(false),
              marginTop: '24px',
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // ── In-call view — full-viewport, no card wrapper. The `key` prop
  // remounts InterviewCall on host reconnect, see negotiationKey. ──
  if (inCall) {
    return (
      <InterviewCall
        key={`call-${negotiationKey}`}
        peerRole="guest"
        localName={guestName.trim()}
        localStream={stream}
        ws={ws}
        iceServers={iceServers}
        onLeave={handleLeaveCall}
      />
    );
  }

  // Round lobby toggle button — red/filled when the control is "off"
  // (muted / camera off), matching the in-call meeting controls.
  const lobbyBtn = (active) => ({
    width: '52px',
    height: '52px',
    borderRadius: '999px',
    border: 'none',
    background: active ? '#ff4d4f' : 'rgba(255,255,255,0.14)',
    color: '#fff',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: active ? '0 6px 18px rgba(255, 77, 79, 0.35)' : 'none',
  });
  const lobbyLabel = { fontSize: '0.72rem', fontWeight: 700, color: '#b9bdc7' };

  return (
    <div style={overlay}>

      <div style={center(isMobile)}>
        <div style={card(isMobile)}>
          <h1 style={heading(isMobile)}>You&apos;re invited</h1>
          <p style={body}>
            {phase === 'waiting'
              ? 'You\'re in — the host will start the recording when they\'re ready.'
              : 'Join the conversation. The host can record the whole thing right from their browser.'}
          </p>

          <div style={videoFrame}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={videoTag}
            />

            {phase === 'checking' && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#cfd1da',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                }}
              >
                Checking the link…
              </div>
            )}

            {phase === 'ready-to-join' && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  gap: '8px',
                }}
              >
                <span aria-hidden="true" style={{ fontSize: '2rem' }}>📷</span>
                <span>Tap Join to share your camera and mic.</span>
              </div>
            )}

            {phase === 'joining' && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.6)',
                  color: '#cfd1da',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                }}
              >
                Asking for camera + mic…
              </div>
            )}

            {phase === 'error' && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.75)',
                  color: '#ff9b9b',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  textAlign: 'center',
                  padding: '20px',
                  gap: '10px',
                }}
              >
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Status pill — mirrors the host's "waiting / connected" line. */}
          {phase === 'waiting' && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                color: hostConnected ? '#bce8c2' : '#cfd1da',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '999px',
                  background: hostConnected ? '#5ad77a' : '#ffd166',
                  boxShadow: hostConnected
                    ? '0 0 0 6px rgba(90, 215, 122, 0.18)'
                    : '0 0 0 6px rgba(255, 209, 102, 0.18)',
                }}
              />
              {hostConnected ? 'Host is here — waiting for them to start.' : 'Waiting for the host…'}
            </div>
          )}

          {phase === 'ready-to-join' && (
            <>
              {/* Name — shown to the host in place of "Guest". Optional. */}
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value.slice(0, 40))}
                placeholder="Your name (optional)"
                aria-label="Your name"
                style={{
                  width: '100%',
                  maxWidth: '320px',
                  margin: '0 auto',
                  padding: '12px 16px',
                  borderRadius: '999px',
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#fff',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  textAlign: 'center',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />

              {/* Pre-join controls — pick how you want to join, Zoom-style. */}
              <div style={{
                display: 'flex', justifyContent: 'center', gap: '28px', marginTop: '4px',
              }}
              >
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                }}
                >
                  <button
                    type="button"
                    onClick={() => setJoinMuted((m) => !m)}
                    aria-pressed={joinMuted}
                    aria-label={joinMuted ? 'Join with microphone off' : 'Join with microphone on'}
                    style={lobbyBtn(joinMuted)}
                  >
                    {joinMuted ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="1" y1="1" x2="23" y2="23" />
                        <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                        <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                      </svg>
                    )}
                  </button>
                  <span style={lobbyLabel}>{joinMuted ? 'Mic off' : 'Mic on'}</span>
                </div>

                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                }}
                >
                  <button
                    type="button"
                    onClick={() => setJoinCameraOff((c) => !c)}
                    aria-pressed={joinCameraOff}
                    aria-label={joinCameraOff ? 'Join with camera off' : 'Join with camera on'}
                    style={lobbyBtn(joinCameraOff)}
                  >
                    {joinCameraOff ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M23 7l-7 5 7 5V7z" />
                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                      </svg>
                    )}
                  </button>
                  <span style={lobbyLabel}>{joinCameraOff ? 'Camera off' : 'Camera on'}</span>
                </div>
              </div>

              <div style={ctaRow}>
                <button type="button" onClick={handleJoin} style={primaryBtn(false)}>
                  Join meeting
                </button>
              </div>
            </>
          )}

          {phase === 'error' && (
            <div style={ctaRow}>
              <button type="button" onClick={() => window.location.reload()} style={secondaryBtn}>
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterviewJoin;
